import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as z from 'zod';

import { loginUserForOpens, manage2faSetupForOpens } from '@/hooks/opens';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import TFAOTPInput from '@/components/settings/security/components/TFAOTPInput';

const loginSchema = z.object({
  username: z
    .string({ required_error: 'Username is required' })
    .trim()
    .min(1, 'Username is required'),
  password: z
    .string({ required_error: 'Password is required' })
    .min(1, 'Password is required'),
});

const otpSchema = z.object({
  code: z
    .string({ required_error: 'Enter the 6-digit code' })
    .regex(/^\d{6}$/g, 'Enter a valid 6-digit code'),
});

// Completion message component
function CompletionMessage({
  message,
  isSuccess,
  showDeviceApprovalAlert,
  onClose,
}) {
  return (
    <div className='flex flex-col items-center justify-center space-y-4 py-8'>
      <div
        className={`flex h-16 w-16 items-center justify-center rounded-full ${
          isSuccess ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
        }`}
      >
        {isSuccess ? (
          <svg
            className='h-8 w-8'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M5 13l4 4L19 7'
            />
          </svg>
        ) : (
          <svg
            className='h-8 w-8'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M6 18L18 6M6 6l12 12'
            />
          </svg>
        )}
      </div>
      <div
        className={`text-center font-medium ${
          isSuccess ? 'text-green-600' : 'text-red-600'
        }`}
      >
        {message}
      </div>

      {showDeviceApprovalAlert && isSuccess && (
        <div className='rounded-md border border-amber-200 bg-amber-50 p-3 text-center'>
          <div className='text-sm font-medium text-amber-800'>
            📱 Device Approval Required
          </div>
          <div className='mt-1 text-xs text-amber-700'>
            Please check your Instagram app or existing login session to approve
            this device for automated opens.
          </div>
        </div>
      )}

      <Button onClick={onClose} className='w-full'>
        Close
      </Button>
    </div>
  );
}

// Login form component
function LoginForm({ onSuccess, onTwoFactorRequired }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '' },
  });

  const handleLogin = async (values) => {
    setLoading(true);
    setError(null);
    try {
      const res = await loginUserForOpens(values.username, values.password);
      const payload = res.data;
      const status = payload.status;

      if (status === 'success') {
        const successMsg = 'Instagram login successful';
        toast.success(successMsg);
        onSuccess(successMsg);
      } else if (status === '2fa_required') {
        // Data might be directly in payload or under payload.data
        const data = payload.data || payload;

        toast.info('2FA verification required');
        onTwoFactorRequired({
          twoFactorIdentifier: data.two_factor_identifier,
          verificationMethod: data.verification_method ?? '0',
        });
      } else {
        setError('Login failed');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to login';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Autofocus username on mount
    const el = document.getElementById('opens-username');
    el?.focus?.();
  }, []);

  return (
    <Form {...form}>
      <form className='space-y-4' onSubmit={form.handleSubmit(handleLogin)}>
        <FormField
          control={form.control}
          name='username'
          render={({ field }) => (
            <FormItem>
              <FormLabel htmlFor='opens-username'>Instagram Username</FormLabel>
              <FormControl>
                <Input id='opens-username' autoComplete='username' {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='password'
          render={({ field }) => (
            <FormItem>
              <FormLabel htmlFor='opens-password'>Instagram Password</FormLabel>
              <FormControl>
                <div className='relative'>
                  <Input
                    id='opens-password'
                    type={showPassword ? 'text' : 'password'}
                    autoComplete='current-password'
                    className='pr-10'
                    {...field}
                  />
                  <button
                    type='button'
                    className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none'
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className='h-4 w-4' />
                    ) : (
                      <Eye className='h-4 w-4' />
                    )}
                  </button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {error && <div className='text-xs text-red-500'>{error}</div>}

        {loading && (
          <div className='rounded-md border border-blue-200 bg-blue-50 p-3 text-center'>
            <div className='text-sm font-medium text-blue-800'>
              🔧 Setting up your Instagram Opens system...
            </div>
            <div className='mt-1 text-xs text-blue-700'>
              This may take a couple of seconds as we configure your account for
              automated opens. Please wait.
            </div>
          </div>
        )}

        <Button type='submit' className='w-full' disabled={loading}>
          {loading ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </Form>
  );
}

// OTP form component
function OTPForm({
  twoFactorIdentifier,
  verificationMethod,
  onSuccess,
  onBack,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const form = useForm({
    resolver: zodResolver(otpSchema),
    defaultValues: { code: '' },
  });

  const handleSubmitOtp = async (values) => {
    if (!twoFactorIdentifier) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await manage2faSetupForOpens(
        twoFactorIdentifier,
        verificationMethod,
        values.code
      );
      const payload = res.data;
      const status = payload.status;
      const message = payload.message;

      if (status === 'success') {
        const successMsg = message || 'Instagram login successful';
        toast.success(successMsg);
        onSuccess(successMsg);
      } else {
        setError(message || '2FA verification failed');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to verify 2FA';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    // Autofocus OTP input on mount
    // The InputOTP component renders multiple inputs, focus the first one
    const otpInput = document.querySelector('input[inputmode="numeric"]');
    if (otpInput) {
      otpInput.focus();
    }
  }, []);

  return (
    <Form {...form}>
      <form className='space-y-4' onSubmit={form.handleSubmit(handleSubmitOtp)}>
        <FormField
          control={form.control}
          name='code'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Enter 2FA code</FormLabel>
              <FormControl>
                <TFAOTPInput
                  value={field.value}
                  onChange={field.onChange}
                  isDisabled={submitting}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {error && <div className='text-xs text-red-500'>{error}</div>}

        {submitting && (
          <div className='rounded-md border border-blue-200 bg-blue-50 p-3 text-center'>
            <div className='text-sm font-medium text-blue-800'>
              🔐 Verifying and finalizing setup...
            </div>
            <div className='mt-1 text-xs text-blue-700'>
              Please wait while we complete the authentication process.
            </div>
          </div>
        )}

        <div className='flex items-center gap-2'>
          <Button type='button' variant='outline' onClick={onBack}>
            Back
          </Button>
          <Button type='submit' disabled={submitting} className='flex-1'>
            {submitting ? 'Verifying…' : 'Verify'}
          </Button>
        </div>
      </form>
    </Form>
  );
}

// Main component
export default function OpensLogin() {
  const [view, setView] = useState('login'); // 'login' | 'otp' | 'complete'
  const [completionMessage, setCompletionMessage] = useState('');
  const [completionSuccess, setCompletionSuccess] = useState(false);
  const [showDeviceApprovalAlert, setShowDeviceApprovalAlert] = useState(false);
  const [twoFactorData, setTwoFactorData] = useState({
    twoFactorIdentifier: null,
    verificationMethod: null,
  });

  const postMessage = (payload, message, isSuccess = true) => {
    // Show completion message
    setView('complete');
    setCompletionMessage(message);
    setCompletionSuccess(isSuccess);

    // Post message to parent window (no automatic closing)
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          { type: 'OPENS_AUTHENTICATED', ...payload },
          window.location.origin
        );
      }
    } catch (e) {
      /* noop */
    }
  };

  const handleClose = () => {
    // Reload parent window and close popup
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.location.reload();
      }
    } catch (e) {
      /* noop */
    }

    try {
      window.close();
    } catch (e) {
      /* noop */
    }
  };

  const handleLoginSuccess = (message) => {
    setShowDeviceApprovalAlert(true);
    postMessage(
      {
        success: true,
        message,
      },
      message,
      true
    );
  };

  const handleTwoFactorRequired = (data) => {
    setTwoFactorData(data);
    setView('otp');
  };

  const handleOtpSuccess = (message) => {
    // Show device approval alert for OTP success too
    setShowDeviceApprovalAlert(true);
    postMessage(
      {
        success: true,
        message,
      },
      message,
      true
    );
  };

  const handleBack = () => {
    setView('login');
    setTwoFactorData({
      twoFactorIdentifier: null,
      verificationMethod: null,
    });
  };

  return (
    <div className='mx-auto max-w-sm p-6'>
      <div className='mb-6 text-center'>
        <div className='text-lg font-semibold'>
          Authenticate Instagram Opens
        </div>
        <div className='text-xs text-muted-foreground'>
          Sign in using your Instagram account to continue
        </div>
      </div>

      {view === 'complete' ? (
        <CompletionMessage
          message={completionMessage}
          isSuccess={completionSuccess}
          showDeviceApprovalAlert={showDeviceApprovalAlert}
          onClose={handleClose}
        />
      ) : view === 'login' ? (
        <LoginForm
          onSuccess={handleLoginSuccess}
          onTwoFactorRequired={handleTwoFactorRequired}
        />
      ) : (
        <OTPForm
          twoFactorIdentifier={twoFactorData.twoFactorIdentifier}
          verificationMethod={twoFactorData.verificationMethod}
          onSuccess={handleOtpSuccess}
          onBack={handleBack}
        />
      )}
    </div>
  );
}
